import React, {useCallback, useEffect, useRef, useState} from "react";
import {ReactNodeViewRenderer, NodeViewWrapper} from "@tiptap/react";
import Image from "@tiptap/extension-image";

const MIN_WIDTH = 40;

/**
 * Erweiterung von @tiptap/extension-image um per Maus ziehbare
 * Resize-Anfasser (wie in Word/Outlook), inkl. proportionaler Skalierung.
 * Name und setImage-Command bleiben identisch zu Image - der bestehende
 * Upload-Code in HtmlEditor.jsx (editor.chain().focus().setImage(...))
 * funktioniert dadurch unverändert weiter.
 */
const ResizableImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: null,
                parseHTML: (element) => {
                    const value = element.getAttribute("width");

                    return value ? parseInt(value, 10) : null;
                },
                renderHTML: (attributes) => {
                    if (!attributes.width) {
                        return {};
                    }

                    return {width: String(attributes.width)};
                },
            },
            height: {
                default: null,
                parseHTML: (element) => {
                    const value = element.getAttribute("height");

                    return value ? parseInt(value, 10) : null;
                },
                renderHTML: (attributes) => {
                    if (!attributes.height) {
                        return {};
                    }

                    return {height: String(attributes.height)};
                },
            },
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute("style"),
                renderHTML: (attributes) => {
                    if (!attributes.width) {
                        return {};
                    }

                    // Inline-Style zusätzlich zu den width/height-Attributen
                    // für moderne Mail-Clients - Outlook/Word respektieren
                    // dagegen bevorzugt die reinen HTML-Attribute (daher
                    // Zahlen ohne "px" oben, kein Verlass auf CSS).
                    const heightPart = attributes.height ? `${attributes.height}px` : "auto";

                    return {style: `width:${attributes.width}px;height:${heightPart}`};
                },
            },
        };
    },

    addNodeView() {
        return ReactNodeViewRenderer(ResizableImageView);
    },
});

function ResizableImageView({node, updateAttributes, selected}) {
    const {src, alt, title, width, height} = node.attrs;
    const imgRef = useRef(null);
    const wrapperRef = useRef(null);
    // Hält die aktuell registrierten document-Listener, damit der Cleanup
    // (Unmount oder mouseup) garantiert exakt dieselbe Funktionsreferenz
    // entfernt, die auch registriert wurde - unabhängig davon, ob
    // updateAttributes sich zwischen Renders ändert.
    const activeListenersRef = useRef(null);
    const [liveSize, setLiveSize] = useState(null);

    const removeActiveListeners = useCallback(() => {
        const listeners = activeListenersRef.current;
        if (!listeners) {
            return;
        }
        document.removeEventListener("mousemove", listeners.onMove);
        document.removeEventListener("mouseup", listeners.onUp);
        activeListenersRef.current = null;
    }, []);

    // Beim Unmount (z. B. Bild wird gelöscht, während gerade gezogen
    // wird) auf jeden Fall die Listener abräumen - kein Event-Leak.
    useEffect(() => {
        return () => removeActiveListeners();
    }, [removeActiveListeners]);

    const startDrag = (corner) => (event) => {
        event.preventDefault();
        event.stopPropagation();

        const img = imgRef.current;
        const wrapper = wrapperRef.current;
        if (!img || !wrapper) {
            return;
        }

        const startWidth = width || img.getBoundingClientRect().width;
        const startHeight = height || img.getBoundingClientRect().height;
        const ratio = startWidth / startHeight;

        const editorEl = wrapper.closest(".ProseMirror");
        const maxWidth = editorEl ? editorEl.getBoundingClientRect().width : startWidth * 4;

        const drag = {
            corner,
            startX: event.clientX,
            startY: event.clientY,
            startWidth,
            startHeight,
            ratio,
            maxWidth,
        };

        const onMove = (moveEvent) => {
            const dx = moveEvent.clientX - drag.startX;
            const dy = moveEvent.clientY - drag.startY;

            // Vorzeichen-Logik: bei den rechten Ecken (Corner ohne "w")
            // vergrößert ein Ziehen nach rechts (positives dx) das Bild;
            // bei den linken Ecken ("w") ist es umgekehrt - dort bedeutet
            // Ziehen nach links (negatives dx) eine Vergrößerung, daher
            // wird dx dort mit -1 multipliziert. Für die Y-Achse gilt
            // dasselbe für obere ("n") vs. untere Ecken. Von den beiden
            // Achsen zählt nur die mit der größeren Zugbewegung
            // (dominante Achse), die Gegenachse wird über das feste
            // Seitenverhältnis der Startgröße nachgezogen - so bleibt
            // die Skalierung immer proportional.
            const signedDx = drag.corner.includes("w") ? -dx : dx;
            const signedDy = drag.corner.includes("n") ? -dy : dy;

            const delta = Math.abs(signedDx) > Math.abs(signedDy) ? signedDx : signedDy * drag.ratio;

            let newWidth = drag.startWidth + delta;
            newWidth = Math.max(MIN_WIDTH, Math.min(newWidth, drag.maxWidth));
            const newHeight = Math.round(newWidth / drag.ratio);

            setLiveSize({width: Math.round(newWidth), height: newHeight});
        };

        const onUp = () => {
            setLiveSize((current) => {
                if (current) {
                    updateAttributes({width: current.width, height: current.height});
                }

                return null;
            });
            removeActiveListeners();
        };

        removeActiveListeners();
        activeListenersRef.current = {onMove, onUp};
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    const displayWidth = liveSize?.width ?? width ?? undefined;
    const displayHeight = liveSize?.height ?? height ?? undefined;

    const corners = [
        {key: "nw", cursor: "nwse-resize", style: {top: -5, left: -5}},
        {key: "ne", cursor: "nesw-resize", style: {top: -5, right: -5}},
        {key: "sw", cursor: "nesw-resize", style: {bottom: -5, left: -5}},
        {key: "se", cursor: "nwse-resize", style: {bottom: -5, right: -5}},
    ];

    return (
        <NodeViewWrapper
            className="resizable-image-wrapper"
            style={{display: "inline-block", position: "relative"}}
            ref={wrapperRef}
        >
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                title={title}
                draggable={false}
                width={displayWidth}
                height={displayHeight}
                style={{
                    width: displayWidth ? `${displayWidth}px` : undefined,
                    height: displayHeight ? `${displayHeight}px` : "auto",
                    display: "block",
                }}
            />
            {selected && corners.map((corner) => (
                <span
                    key={corner.key}
                    className="resizable-image-handle"
                    style={{...corner.style, cursor: corner.cursor}}
                    onMouseDown={startDrag(corner.key)}
                />
            ))}
            {selected && <span className="resizable-image-outline"/>}
        </NodeViewWrapper>
    );
}

export default ResizableImage;
